import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, ArrowLeft, LogOut, Users, Trophy, Eye, LogIn as LogInIcon,
  Activity, UserCheck, CalendarDays, BarChart3, Ban, CheckCircle2, Trash2, Search,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

async function adminApi(path, options = {}) {
  const res = await fetch(`${API}/api/admin/${path}`, { credentials: "include", ...options });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    const err = new Error(e.detail || "Request failed");
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const METRIC_COLORS = {
  visitors: "bg-neon-cyan",
  logins: "bg-emerald-400",
  newUsers: "bg-violet-400",
  tournaments: "bg-amber-400",
};

function MetricCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-surface p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-11 h-11 rounded-lg bg-neon-cyan/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-neon-cyan" />
      </div>
      <div className="min-w-0">
        <p className="text-white/50 text-xs uppercase tracking-wide truncate">{label}</p>
        <p className="text-white font-heading text-2xl font-bold">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function BarChart({ labels, values, colorClass }) {
  const [selected, setSelected] = useState(null);
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-1 h-48 w-full overflow-x-auto pb-2 pt-7" data-testid="analytics-chart">
      {values.map((v, i) => (
        <button
          type="button"
          key={labels[i]}
          data-testid={`analytics-bar-${i}`}
          onClick={() => setSelected((prev) => (prev === i ? null : i))}
          className="flex-1 min-w-[8px] flex flex-col items-center justify-end h-full group relative cursor-pointer"
        >
          <span
            className={`absolute -top-6 text-[11px] font-bold px-1.5 rounded whitespace-nowrap transition-opacity ${
              selected === i ? "opacity-100 text-white bg-black/80" : "opacity-0 group-hover:opacity-100 text-white bg-black/80"
            }`}
            data-testid={`analytics-bar-value-${i}`}
          >
            {selected === i ? v : `${labels[i].slice(5)}: ${v}`}
          </span>
          <div
            className={`w-full rounded-t ${colorClass} transition-all ${selected === i ? "ring-2 ring-white/60" : ""}`}
            style={{ height: `${(v / max) * 100}%`, minHeight: v > 0 ? "3px" : "0" }}
          />
        </button>
      ))}
    </div>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return String(v).slice(0, 10); }
}
function fmtDateTime(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { adminLogout } = useAuth();
  const { t } = useLanguage();

  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [metric, setMetric] = useState("visitors");
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [tourSearch, setTourSearch] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAuthError = useCallback((err) => {
    if (err.status === 401) {
      toast.error(t("admin.needVerify"));
      navigate("/profile");
      return true;
    }
    if (err.status === 403) {
      navigate("/");
      return true;
    }
    return false;
  }, [navigate, t]);

  // Initial gate: load overview; if not verified -> redirect
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await adminApi("overview");
        if (active) { setOverview(data); setLoading(false); }
      } catch (err) {
        if (!handleAuthError(err) && active) { toast.error(err.message); setLoading(false); }
      }
    })();
    return () => { active = false; };
  }, [handleAuthError]);

  // Lazy-load per tab
  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        if (tab === "analytics" && !analytics) setAnalytics(await adminApi("analytics?days=30"));
        if (tab === "logs") setLogs(await adminApi("logs?limit=100"));
      } catch (err) { if (!handleAuthError(err)) toast.error(err.message); }
    })();
  }, [tab, loading, analytics, handleAuthError]);

  const loadUsers = useCallback(async (q = "") => {
    try { setUsers(await adminApi(`users?search=${encodeURIComponent(q)}`)); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.message); }
  }, [handleAuthError]);

  const loadTournaments = useCallback(async (q = "") => {
    try { setTournaments(await adminApi(`tournaments?search=${encodeURIComponent(q)}`)); }
    catch (err) { if (!handleAuthError(err)) toast.error(err.message); }
  }, [handleAuthError]);

  useEffect(() => { if (!loading && tab === "users") loadUsers(""); }, [tab, loading, loadUsers]);
  useEffect(() => { if (!loading && tab === "tournaments") loadTournaments(""); }, [tab, loading, loadTournaments]);

  const toggleSuspend = async (u) => {
    try {
      await adminApi(`users/${u.user_id}/${u.suspended ? "unsuspend" : "suspend"}`, { method: "POST" });
      toast.success(u.suspended ? t("admin.unsuspendedToast") : t("admin.suspendedToast"));
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, suspended: !x.suspended } : x));
    } catch (err) { if (!handleAuthError(err)) toast.error(err.message); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi(`tournaments/${deleteTarget.id}`, { method: "DELETE" });
      toast.success(t("admin.deletedToast"));
      setTournaments((prev) => prev.filter((x) => x.id !== deleteTarget.id));
    } catch (err) { if (!handleAuthError(err)) toast.error(err.message); }
    finally { setDeleteTarget(null); }
  };

  const exitAdmin = async () => { await adminLogout(); navigate("/"); };

  const TABS = [
    { id: "overview", label: t("admin.tabOverview"), icon: BarChart3 },
    { id: "analytics", label: t("admin.tabAnalytics"), icon: Activity },
    { id: "users", label: t("admin.tabUsers"), icon: Users },
    { id: "tournaments", label: t("admin.tabTournaments"), icon: Trophy },
    { id: "logs", label: t("admin.tabLogs"), icon: CalendarDays },
  ];

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center animate-pulse">
          <Crown className="w-6 h-6 text-yellow-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-sm sticky top-0 z-20">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-tight truncate">{t("admin.title")}</h1>
              <p className="text-white/40 text-xs truncate hidden sm:block">{t("admin.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="admin-back-btn"
              className="text-white/60 hover:text-white hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t("admin.backToApp")}</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={exitAdmin} data-testid="admin-exit-btn"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
              <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t("admin.exitAdmin")}</span>
            </Button>
          </div>
        </div>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto -mb-px">
            {TABS.map((tb) => (
              <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  tab === tb.id ? "border-neon-cyan text-neon-cyan" : "border-transparent text-white/50 hover:text-white/80"
                }`}>
                <tb.icon className="w-4 h-4" />{tb.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {tab === "overview" && overview && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="overview-grid">
            <MetricCard icon={Eye} label={t("admin.totalVisitors")} value={overview.totalVisitors} testid="metric-total-visitors" />
            <MetricCard icon={UserCheck} label={t("admin.uniqueVisitors")} value={overview.uniqueVisitors} testid="metric-unique-visitors" />
            <MetricCard icon={Users} label={t("admin.registeredUsers")} value={overview.registeredUsers} testid="metric-registered-users" />
            <MetricCard icon={LogInIcon} label={t("admin.totalLogins")} value={overview.totalLogins} testid="metric-total-logins" />
            <MetricCard icon={Activity} label={t("admin.activeUsers")} value={overview.activeUsers} testid="metric-active-users" />
            <MetricCard icon={Trophy} label={t("admin.totalTournaments")} value={overview.totalTournaments} testid="metric-total-tournaments" />
            <MetricCard icon={CalendarDays} label={t("admin.tournamentsToday")} value={overview.tournamentsToday} testid="metric-tournaments-today" />
            <MetricCard icon={CalendarDays} label={t("admin.tournamentsMonth")} value={overview.tournamentsMonth} testid="metric-tournaments-month" />
            <MetricCard icon={Ban} label={t("admin.suspendedUsers")} value={overview.suspendedUsers} testid="metric-suspended-users" />
          </div>
        )}

        {tab === "analytics" && (
          <div className="card-surface p-5" data-testid="analytics-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-white/50 text-sm">{t("admin.last30days")}</p>
              <div className="flex gap-1 flex-wrap">
                {["visitors", "logins", "newUsers", "tournaments"].map((m) => (
                  <button key={m} data-testid={`analytics-metric-${m}`} onClick={() => setMetric(m)}
                    className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                      metric === m ? "bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/40" : "bg-white/5 text-white/50 border border-transparent hover:text-white/80"
                    }`}>
                    {t(`admin.${m === "tournaments" ? "tournaments" : m}`)}
                  </button>
                ))}
              </div>
            </div>
            {analytics ? (
              <BarChart labels={analytics.labels} values={analytics[metric] || []} colorClass={METRIC_COLORS[metric]} />
            ) : (
              <p className="text-white/40 text-sm py-12 text-center">{t("admin.loading")}</p>
            )}
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-4" data-testid="users-panel">
            <form onSubmit={(e) => { e.preventDefault(); loadUsers(userSearch); }} className="relative max-w-md">
              <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="user-search-input" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                placeholder={t("admin.searchUsers")} className="input-dark pl-9" />
            </form>
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-xs uppercase border-b border-white/10">
                    <th className="text-left font-semibold px-4 py-3">{t("admin.user")}</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">{t("admin.provider")}</th>
                    <th className="text-center font-semibold px-4 py-3">{t("admin.tournaments")}</th>
                    <th className="text-left font-semibold px-4 py-3">{t("admin.status")}</th>
                    <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("admin.joined")}</th>
                    <th className="text-right font-semibold px-4 py-3">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody data-testid="users-table-body">
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/5" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3 min-w-0">
                        <div className="flex items-center gap-2">
                          {u.is_owner && <Crown className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate max-w-[180px]">{u.name || "—"}</p>
                            <p className="text-white/40 text-xs truncate max-w-[180px]">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-white/60 capitalize">{u.auth_provider}</td>
                      <td className="px-4 py-3 text-center text-white/70">{u.tournament_count}</td>
                      <td className="px-4 py-3">
                        {u.suspended ? (
                          <span className="text-red-400 text-xs font-semibold">{t("admin.suspended")}</span>
                        ) : (
                          <span className="text-emerald-400 text-xs font-semibold">{t("admin.active")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-white/50 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {!u.is_owner && (
                          <Button size="sm" variant="ghost" data-testid={`suspend-btn-${u.user_id}`} onClick={() => toggleSuspend(u)}
                            className={u.suspended ? "text-emerald-400 hover:bg-emerald-500/10" : "text-red-400 hover:bg-red-500/10"}>
                            {u.suspended ? <CheckCircle2 className="w-4 h-4 sm:mr-1" /> : <Ban className="w-4 h-4 sm:mr-1" />}
                            <span className="hidden sm:inline">{u.suspended ? t("admin.unsuspend") : t("admin.suspend")}</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-white/40">{t("admin.noData")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "tournaments" && (
          <div className="space-y-4" data-testid="tournaments-panel">
            <form onSubmit={(e) => { e.preventDefault(); loadTournaments(tourSearch); }} className="relative max-w-md">
              <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="tournament-search-input" value={tourSearch} onChange={(e) => setTourSearch(e.target.value)}
                placeholder={t("admin.searchTournaments")} className="input-dark pl-9" />
            </form>
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-xs uppercase border-b border-white/10">
                    <th className="text-left font-semibold px-4 py-3">{t("admin.name")}</th>
                    <th className="text-left font-semibold px-4 py-3">{t("admin.code")}</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">{t("admin.format")}</th>
                    <th className="text-center font-semibold px-4 py-3 hidden sm:table-cell">{t("admin.players")}</th>
                    <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("admin.created")}</th>
                    <th className="text-right font-semibold px-4 py-3">{t("admin.actions")}</th>
                  </tr>
                </thead>
                <tbody data-testid="tournaments-table-body">
                  {tournaments.map((tr) => (
                    <tr key={tr.id} className="border-b border-white/5" data-testid={`tournament-row-${tr.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/t/${tr.id}`)} className="text-white font-medium hover:text-neon-cyan transition-colors truncate max-w-[160px] text-left block">
                          {tr.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-data text-neon-cyan">{tr.code}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-white/60 capitalize">{tr.format}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-center text-white/70">{tr.participants}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-white/50 text-xs">{fmtDate(tr.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" data-testid={`delete-tournament-btn-${tr.id}`} onClick={() => setDeleteTarget(tr)}
                          className="text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t("admin.delete")}</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {tournaments.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-white/40">{t("admin.noData")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "logs" && (
          <div className="card-surface overflow-x-auto" data-testid="logs-panel">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-xs uppercase border-b border-white/10">
                  <th className="text-left font-semibold px-4 py-3">{t("admin.when")}</th>
                  <th className="text-left font-semibold px-4 py-3">{t("admin.action")}</th>
                  <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">{t("admin.user")}</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("admin.details")}</th>
                </tr>
              </thead>
              <tbody data-testid="logs-table-body">
                {logs.map((l, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="px-4 py-3 text-white/50 text-xs whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                    <td className="px-4 py-3"><span className="text-neon-cyan font-data text-xs">{l.action}</span></td>
                    <td className="px-4 py-3 hidden sm:table-cell text-white/60 text-xs truncate max-w-[180px]">{l.email || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-white/40 text-xs truncate max-w-[240px]">
                      {l.meta && Object.keys(l.meta).length ? JSON.stringify(l.meta) : "—"}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-white/40">{t("admin.noData")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0f0f12] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">{deleteTarget?.name}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">{t("back")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-tournament-btn" onClick={doDelete}
              className="bg-red-500 hover:bg-red-600 text-white">{t("admin.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
